import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { parseCards } from './cards/cardUtils';
import CardImg from './cards/CardImg';
import { roundBook, fmtBook } from '../../utils/casinoBook';
const REMARK_ICON = 'https://wver.sprintstaticdata.com/v230/static/front/img/icons/remark.png'

const CDN = 'https://wver.sprintstaticdata.com/v230/static/front/img';
const AB_CARD_BASE = `${CDN}/andar-bahar-cards/`;

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

function bookOf(exposures, name, sid) {
  return roundBook(exposures?.[`sid:${sid}`] ?? exposures?.[name] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

// Reference owl options (andar-bahar): rtl, margin 0, responsive {0:{items:3}, 768:{items:4}}.
// Owl item width = stage-outer width / items (toFixed(3)), stage width = ceil(sum)
// -> 29.75px on phones (89.25/3), 43.563px on desktop (174.25/4).
const VIDEO_ITEM_W = 29.75;
const VIDEO_ITEMS = (vw) => (vw >= 768 ? 4 : 3);

// Owl Carousel 2 replica (dealt cards) — exact owl DOM + React paging.
const VideoOwl = memo(function VideoOwl({ id, cards }) {
  const outerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [perView, setPerView] = useState(3);
  const [itemW, setItemW] = useState(VIDEO_ITEM_W);

  useLayoutEffect(() => {
    const measure = () => {
      // fractional width like owl's jQuery .width() (clientWidth rounds 174.25 -> 174)
      const w = outerRef.current?.getBoundingClientRect().width || 0;
      if (w <= 0) return;
      const items = VIDEO_ITEMS(window.innerWidth);
      setPerView(items);
      setItemW(Number((w / items).toFixed(3)));
    };
    measure();
    // the strip starts collapsed (.hide-cards width 0, 1s transition) — re-measure once it opens
    const ro = typeof ResizeObserver !== 'undefined' && outerRef.current ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(outerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [cards.length]);

  const prev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
  const next = useCallback(() => setPage((p) => p + 1), []);

  const list = cards.length ? cards : [];
  const maxPage = Math.max(0, Math.ceil(list.length / perView) - 1);
  const cur = Math.min(page, maxPage);
  const atStart = cur <= 0;
  const atEnd = cur >= maxPage;
  // owl-rtl (float:right) => translate positive (right) direction — movement reverse
  const translateX = cur * perView * itemW;
  const stageWidth = Math.ceil(list.length * itemW);

  // koi card nahi -> ek khaali owl-item placeholder (reference)
  if (list.length === 0) {
    return (
      <div id={id} className="ab-slider owl-carousel owl-theme owl-rtl owl-loaded owl-drag">
        <div className="owl-stage-outer" ref={outerRef}>
          <div className="owl-stage" style={{ transform: 'translate3d(0px, 0px, 0px)', transition: 'all', width: `${Math.ceil(itemW)}px` }}>
            <div className="owl-item active" style={{ width: `${itemW}px` }}><div></div></div>
          </div>
        </div>
        <div className="owl-nav disabled">
          <button type="button" role="presentation" className="owl-prev disabled"><span aria-label="Previous">‹</span></button>
          <button type="button" role="presentation" className="owl-next disabled"><span aria-label="Next">›</span></button>
        </div>
        <div className="owl-dots disabled"></div>
      </div>
    );
  }

  return (
    <div id={id} className="ab-slider owl-carousel owl-theme owl-rtl owl-loaded owl-drag">
      <div className="owl-stage-outer" ref={outerRef}>
        <div
          className="owl-stage"
          style={{ transform: `translate3d(${translateX}px, 0px, 0px)`, transition: 'all 0.25s ease', width: `${stageWidth}px` }}
        >
          {list.map((code, i) => {
            const active = i >= cur * perView && i < (cur + 1) * perView;
            return (
              <div key={i} className={`owl-item${active ? ' active' : ''}`} style={{ width: `${itemW}px` }}>
                <div className="item">
                  <span><CardImg code={code} /></span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={`owl-nav${list.length <= perView ? ' disabled' : ''}`}>
        <button type="button" role="presentation" className={`owl-prev${atStart ? ' disabled' : ''}`} onClick={atStart ? undefined : prev}>
          <span aria-label="Previous">‹</span>
        </button>
        <button type="button" role="presentation" className={`owl-next${atEnd ? ' disabled' : ''}`} onClick={atEnd ? undefined : next}>
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled"></div>
    </div>
  );
});

// casino-video dealt-cards overlay — andar/bahar ki do owl strips.
// andar = gameData.aall, bahar = gameData.ball. Owl newest-card-pehle dikhata hai,
// isliye reverse (reference DOM: aall="7DD,7SS,KSS" -> andar [KSS,7SS,7DD]).
export const AbVideoCardsAB20 = memo(function AbVideoCardsAB20({ andar = '', bahar = '', showCards = true, onToggle }) {
  const andarCards = parseCards(andar).reverse();
  const baharCards = parseCards(bahar).reverse();
  // koi card dealt nahi (aall/ball empty) -> hide-cards (reference), ya manual toggle off
  const hasCards = andarCards.length > 0 || baharCards.length > 0;
  return (
    <div className={`casino-video-cards${showCards && hasCards ? '' : ' hide-cards'}`}>
      <div className="casino-cards-shuffle" onClick={onToggle}>
        <i className="fas fa-grip-lines-vertical"></i>
      </div>
      {' '}
      <div className="casino-video-cards-container">
        <VideoOwl id="andarSlider" cards={andarCards} />
        {' '}
        <VideoOwl id="baharSlider" cards={baharCards} />
      </div>
    </div>
  );
});

function MinMax({ min = 10, max = 10000 }) {
  return (
    <div className="casino-min-max">
      R:
      <span>{fmtAmt(min)}</span>
      -
      <span>{fmtAmt(max)}</span>
    </div>
  );
}

// 13-card Andar/Bahar grid. Image number gameData.ares (Andar) / bres (Bahar)
// se aata hai (e.g. "0,0,0,4,5,6,7,0,9,0,11,0,0"); 0.png = face-down.
const CardsBetBox = memo(function CardsBetBox({ boxClass, titleClass, title, items, positions, rankBase, dealt, onBet, exposures }) {
  return (
    <div className={`${boxClass} text-center`}>
      <h5 className={`w-100 text-center ${titleClass}`}>
        {title}
        <MinMax />
      </h5>
      {' '}
      {items.map((item, idx) => {
        // deal se pehle har slot apni rank face-up (Andar 1..13, Bahar 21..33);
        // deal shuru hone ke baad ares/bres ka result code (0 = face-down)
        const imgNum = dealt ? (positions[idx] || 0) : rankBase + idx;
        const book = bookOf(exposures, item.nat, item.sid);
        // ab20 items me sirf back price (b) hota hai; b<=0 => suspended
        const isSusp = item.gstatus === 'SUSPENDED' || item.gstatus === '0' || !(parseFloat(item.b) > 0);
        return (
          <div
            key={item.nat || idx}
            className="casino-card-item"
            onClick={isSusp ? undefined : () => onBet && onBet(item.sid, 'back', item.b, item)}
          >
            <div className="card-image">
              <img src={`${AB_CARD_BASE}${imgNum}.png`} alt={item.nat} />
            </div>
            {' '}
            <div className={`casino-book ${bookCls(book)}`}>{fmtBook(book)}</div>
          </div>
        );
      })}
    </div>
  );
});

const BetTableAB20 = memo(function BetTableAB20({ gameData, onBet, exposures }) {
  const sub = gameData?.sub ?? [];
  const remark = gameData?.remark ?? '';

  const andarItems = sub.filter((c) => String(c.nat).startsWith('Andar'));
  const baharItems = sub.filter((c) => String(c.nat).startsWith('Bahar'));

  // Grid image positions API se seedhe: ares (Andar) / bres (Bahar) — per-rank
  // result codes (Andar 1..13, Bahar 21..33, 0 = face-down). (frontend
  // BetTableAB20 se ported — card-parity guess NAHI.)
  const andarPositions = useMemo(() => String(gameData?.ares || '').split(',').map(Number), [gameData?.ares]);
  const baharPositions = useMemo(() => String(gameData?.bres || '').split(',').map(Number), [gameData?.bres]);
  // deal shuru hua ki nahi: koi bhi position > 0 (dono side ek saath flip hote)
  const dealt = useMemo(
    () => andarPositions.some((p) => p > 0) || baharPositions.some((p) => p > 0),
    [andarPositions, baharPositions],
  );

  return (
    <>
      <div className="ab-bg">
        <CardsBetBox
          boxClass="andar-cards-box"
          titleClass="text-playera"
          title="Andar"
          items={andarItems}
          positions={andarPositions}
          rankBase={1}
          dealt={dealt}
          onBet={onBet}
          exposures={exposures}
        />
        <CardsBetBox
          boxClass="bahar-cards-box"
          titleClass="text-playerb"
          title="Bahar"
          items={baharItems}
          positions={baharPositions}
          rankBase={21}
          dealt={dealt}
          onBet={onBet}
          exposures={exposures}
        />
      </div>
      {' '}
      <div className="casino-remark mt-1">
        <div className="remark-icon"><img src={REMARK_ICON} /></div>
        {' '}
        <marquee>{remark}</marquee>
      </div>
    </>
  );
});

export default BetTableAB20;
